/**
 * Dialog for capturing wrap-up disposition codes at the end of a SIM call.
 *
 * @module components/sim/takeSim/WrapUpDialog
 * @author Snowfly
 * @version 1.0.0
 */
import React from "react";
import { makeStyles } from '@material-ui/core/styles';
import Typography from "@material-ui/core/Typography";
import { connect } from 'react-redux';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Button,
} from '@material-ui/core';
import { simWizard } from './SimWizard.js';
import WrapUpTimer from "../WrapUpTimer";
import LinearProgress from "@material-ui/core/LinearProgress";
import PropTypes from "prop-types";

/**
 * A React functional component that represents a dialog prompting users to complete their wrap-up tasks.
 * Includes a progress bar to indicate the status of a file upload process if applicable.
 *
 * @param {boolean} open - Determines whether the dialog is open or closed.
 * @param {Function} doneButtonHandler - Callback function triggered when the "All Done" button is clicked.
 * @param {number} uploadPercent - The current progress percentage of the file upload (if applicable).
 *
 * @returns {JSX.Element} A styled dialog component designed for user interaction in completing wrap-up activities.
 */
const WrapUpDialog = ({
    open,
    doneButtonHandler,
    uploadPercent,
}) => {
    const useStyles = makeStyles((theme) => simWizard(theme));
    const classes = useStyles();

    const [showProgressBar, setShowProgressBar] = React.useState(false);

    // Closing modal option
    /**
     * Handles the closing of a modal or dialog component.
     *
     * @param {Object} ev - The event object associated with the close action.
     * @param {string} reason - The reason for the close action, such as "backdropClick".
     *
     * If the `reason` is specified and equals "backdropClick" or "escapeKeyDown,
     * the function will exit without performing any actions. Otherwise, it triggers
     * the closure process by setting a progress bar indicator and invoking the
     * `doneButtonHandler` callback if it is defined.
     */
    const handleClose = (ev, reason) => {
        if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
            return;
        }

        setShowProgressBar(true);
        doneButtonHandler && doneButtonHandler();
    };

    return (
        <Dialog
            className={classes.modal}
            open={open}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="xs"
            scroll="paper"
        >
            <DialogTitle className={classes.label} id="form-dialog-title">
                Wrap Up Activities
            </DialogTitle>
            <hr className={classes.hr}/>
            <DialogContent className={classes.modalContent}>
                <WrapUpTimer />

                <Grid container spacing={2}>
                    <Grid item xs={12} className={classes.grid}>
                        <div>
                            <Typography variant="body1" align="left">
                                Please complete any wrap up tasks, then come back here and click the &ldquo;All Done&rdquo; button.
                            </Typography>
                        </div>
                    </Grid>
                    {showProgressBar ? (
                        <Grid item xs={12} className={classes.grid}>
                            <div>
                                <Typography variant="body1" align="left">
                                    Your screen recording is being uploaded... Please wait, this may take some time
                                    depending on the speed and quality of your internet service.
                                </Typography>
                                <br />
                                <LinearProgress value={uploadPercent} color={"primary"} variant={"determinate"}></LinearProgress>
                            </div>
                        </Grid>
                    ) : null}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color="primary"
                    variant="outlined"
                    disabled={showProgressBar}
                >
                    All Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Specifies the type of properties for the WrapUpDialog component.
 *
 * propTypes:
 * - isOpen: A boolean that indicates whether the dialog is open.
 * - title: A string that represents the title of the dialog.
 * - onClose: A function triggered when the dialog is closed.
 * - onConfirm: A function executed when the confirm action is performed.
 * - children: A ReactNode representing the content to be displayed inside the dialog.
 */
WrapUpDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    doneButtonHandler: PropTypes.func.isRequired,
    uploadPercent: PropTypes.number.isRequired,
};

export default connect()(WrapUpDialog);
